import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { TrendingUp, Users, BookOpen, Sparkles, Award, MessageCircle, ChevronDown, ChevronUp, Download } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function PublicDistrictDashboard() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});
  const [downloadingPdf, setDownloadingPdf] = useState(false);

  const handleDownloadPdf = async () => {
    setDownloadingPdf(true);
    try {
      const res = await axios.get(`${API}/public/district-admin/${token}/pdf`, {
        responseType: "blob",
      });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const safe = (data?.educator_first_name || "Educator").replace(/[^A-Za-z0-9_-]/g, "_");
      a.download = `District_Insights_${safe}_${new Date().toISOString().slice(0, 10)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      alert("Could not download the PDF right now. Please try again.");
    } finally {
      setDownloadingPdf(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/public/district-admin/${token}`);
        if (cancelled) return;
        setData(res.data);
        document.title = `District Dashboard · ${res.data.educator_first_name || ""}'s Classes`;
      } catch (err) {
        if (cancelled) return;
        const status = err.response?.status;
        if (status === 410) setError(err.response.data?.detail || "This district link is no longer active.");
        else if (status === 404) setError("This district link could not be found.");
        else setError("We could not load this dashboard right now.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  if (loading)
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-orange-50 flex items-center justify-center">
        <div className="text-purple-700 font-semibold">Loading district dashboard…</div>
      </div>
    );

  if (error)
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-purple-50 flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-lg border border-purple-200 p-8 text-center" data-testid="district-error-card">
          <div className="text-5xl mb-3">🔒</div>
          <h1 className="text-xl font-bold text-slate-900 mb-2">Link unavailable</h1>
          <p className="text-slate-600 text-sm">{error}</p>
        </div>
      </div>
    );

  const totals = data.totals || {};
  const avgs = data.averages || {};
  const share = data.share || {};
  const validThrough = share.expires_at
    ? new Date(share.expires_at).toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" })
    : null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-orange-50 pb-12">
      {/* Hero */}
      <div className="bg-gradient-to-br from-purple-600 via-fuchsia-500 to-orange-500 text-white" data-testid="district-hero">
        <div className="max-w-4xl mx-auto px-5 py-10 sm:py-14">
          <div className="text-[11px] uppercase tracking-[0.18em] opacity-90 font-semibold">
            Lemonade Stand · {share.role_title || "District"} Dashboard
          </div>
          <h1 className="text-3xl sm:text-5xl font-black mt-2 mb-2 leading-tight" data-testid="district-title">
            {data.educator_first_name || "Educator"}'s Classes
          </h1>
          <div className="flex flex-wrap gap-2 items-center text-sm opacity-95">
            <span className="bg-white/20 rounded-full px-3 py-1 font-semibold">
              Last {data.window_days} days
            </span>
            <span className="bg-white/20 rounded-full px-3 py-1">
              {totals.classes || 0} {totals.classes === 1 ? "class" : "classes"} · {totals.students || 0} students
            </span>
            {validThrough && (
              <span className="bg-emerald-500/90 rounded-full px-3 py-1 font-semibold" data-testid="district-valid-through">
                ✓ Valid through {validThrough}
              </span>
            )}
          </div>
          <div className="mt-5">
            <button
              onClick={handleDownloadPdf}
              disabled={downloadingPdf}
              className="inline-flex items-center gap-2 bg-white text-purple-700 hover:bg-purple-50 disabled:opacity-60 disabled:cursor-not-allowed font-bold rounded-xl px-4 py-2.5 text-sm shadow-lg transition-all hover:scale-[1.02]"
              data-testid="district-download-pdf-btn"
            >
              <Download className="w-4 h-4" />
              {downloadingPdf ? "Preparing PDF…" : "Download printable PDF"}
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-5 -mt-6">
        {/* AI District Narrative */}
        {data.narrative && (
          <div className="bg-white rounded-2xl shadow-lg border-l-4 border-purple-500 p-5 sm:p-6 mb-6" data-testid="district-narrative">
            <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider text-purple-700 font-semibold mb-2">
              <Sparkles className="w-3.5 h-3.5" />
              AI district summary
            </div>
            <p className="text-base sm:text-lg text-slate-800 leading-relaxed font-medium">{data.narrative}</p>
          </div>
        )}

        {/* Aggregate KPIs */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 mb-6">
          <KpiBox icon={Users} label="Students" value={totals.students || 0} tone="purple" />
          <KpiBox icon={BookOpen} label="Lessons" value={totals.lessons_completed || 0} sub="this week" tone="amber" />
          <KpiBox icon={Award} label="Avg Completion" value={`${avgs.completion_pct || 0}%`} tone="emerald" />
          <KpiBox icon={TrendingUp} label="Pitch Growth" value={`+${avgs.pitch_delta || 0}%`} sub={`${avgs.pitch_first_pct || 0}% → ${avgs.pitch_best_pct || 0}%`} tone="rose" />
        </div>

        {/* Class cards */}
        <div className="space-y-4">
          <h2 className="text-base font-bold text-slate-900 mt-6 mb-2 flex items-center gap-2">
            <MessageCircle className="w-4 h-4 text-purple-600" />
            Class breakdown ({(data.classes || []).length})
          </h2>
          {(data.classes || []).length === 0 ? (
            <div className="bg-white rounded-2xl border border-slate-200 p-6 text-center text-slate-500 italic">
              No classes set up yet.
            </div>
          ) : (
            data.classes.map((cls, i) => {
              const open = !!expanded[i];
              const ct = cls.totals || {};
              const cc = cls.completion || {};
              const cp = cls.pitch_growth || {};
              return (
                <div key={`${cls.class_id}-${i}`} className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden" data-testid={`district-class-${i}`}>
                  <button
                    onClick={() => setExpanded((e) => ({ ...e, [i]: !open }))}
                    className="w-full text-left p-4 sm:p-5 hover:bg-slate-50 transition-colors"
                  >
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                      <div className="min-w-0 flex-1">
                        <h3 className="font-extrabold text-slate-900 text-lg truncate">{cls.class_name}</h3>
                        {cls.description && <p className="text-sm text-slate-500 mt-0.5 line-clamp-1">{cls.description}</p>}
                        <div className="flex flex-wrap gap-2 mt-2.5 text-xs">
                          <Pill tone="purple">{ct.students || 0} students</Pill>
                          <Pill tone="amber">{ct.lessons_completed || 0} lessons / week</Pill>
                          <Pill tone="emerald">{cc.avg_pct || 0}% completion</Pill>
                          <Pill tone="rose">+{cp.delta || 0}% pitch growth</Pill>
                        </div>
                      </div>
                      <div className="text-slate-400 flex-shrink-0">
                        {open ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                      </div>
                    </div>
                  </button>
                  {open && (
                    <div className="border-t border-slate-100 p-4 sm:p-5 bg-slate-50/40 space-y-4">
                      {cls.narrative && (
                        <div className="bg-amber-50 border-l-4 border-amber-300 px-3 py-2.5 rounded-r-lg text-sm text-slate-800 leading-relaxed">
                          {cls.narrative}
                        </div>
                      )}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <div className="text-[11px] uppercase tracking-wider text-slate-500 font-bold mb-1.5">
                            Top question themes
                          </div>
                          {(cls.top_themes || []).length === 0 ? (
                            <p className="text-sm text-slate-500 italic">None this week.</p>
                          ) : (
                            <ul className="space-y-1 text-sm">
                              {cls.top_themes.map((t, j) => (
                                <li key={`${t.label}-${j}`}>
                                  <span className="font-bold text-purple-700">{t.label}</span>{" "}
                                  <span className="text-slate-500">({t.count})</span>
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                        <div>
                          <div className="text-[11px] uppercase tracking-wider text-slate-500 font-bold mb-1.5">
                            Top pitch movers
                          </div>
                          {(cls.top_movers || []).length === 0 ? (
                            <p className="text-sm text-slate-500 italic">No attempts this week.</p>
                          ) : (
                            <ul className="space-y-1 text-sm">
                              {cls.top_movers.map((m, j) => (
                                <li key={`${m.name}-${j}`}>
                                  <span className="font-bold text-slate-900">{m.name}</span>{" "}
                                  <span className="text-emerald-600 font-semibold">+{m.delta}%</span>{" "}
                                  <span className="text-slate-500">({m.first_pct}% → {m.best_pct}%)</span>
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>

        <div className="text-center text-xs text-slate-500 mt-8 px-4 leading-relaxed">
          Read-only district summary. First names only — no email, last name, or internal IDs are shared.
          <br />
          Lemonade Stand · Standards-aligned · Nevada CTE / Perkins V · CCTC
        </div>
      </div>
    </div>
  );
}

function KpiBox({ icon: Icon, label, value, sub, tone }) {
  const tones = {
    amber: "from-amber-50 to-orange-50 border-amber-200 text-amber-900",
    purple: "from-purple-50 to-fuchsia-50 border-purple-200 text-purple-900",
    emerald: "from-emerald-50 to-green-50 border-emerald-200 text-emerald-900",
    rose: "from-rose-50 to-pink-50 border-rose-200 text-rose-900",
  };
  return (
    <div className={`bg-gradient-to-br ${tones[tone] || tones.purple} rounded-2xl border-2 p-5`}>
      <div className="flex items-center gap-2 mb-1.5 text-xs uppercase tracking-wider opacity-80">
        <Icon className="w-3.5 h-3.5" />
        {label}
      </div>
      <div className="text-3xl font-extrabold leading-tight">{value}</div>
      {sub && <div className="text-xs opacity-70 mt-1">{sub}</div>}
    </div>
  );
}

function Pill({ children, tone }) {
  const tones = {
    amber: "bg-amber-100 text-amber-800",
    purple: "bg-purple-100 text-purple-800",
    emerald: "bg-emerald-100 text-emerald-800",
    rose: "bg-rose-100 text-rose-800",
  };
  return (
    <span className={`rounded-full px-2 py-0.5 font-semibold ${tones[tone] || tones.purple}`}>
      {children}
    </span>
  );
}
